import { AxleMenu, MenuItemInfo } from '@/components/AxleMenu';
import { AxleIconButton } from '@/components/buttons/AxleIconButton';
import { HorizontalStack } from '@/components/utils/linearLayouts';
import { CancelVisitDialog } from '@/components/visits/dialogs/CancelVisit/CancelVisitDialog';
import { useEditFulfillmentWindowAction } from '@/components/visits/dialogs/EditFulfillmentWindow/useEditFulfillmentWindowAction';
import { useEditVisitServicesAction } from '@/components/visits/dialogs/EditVisitServices/EditVisitServicesButton';
import { EntrySource } from '@/components/visits/dialogs/ReassignVisits/v3/tracking/entrySource';
import {
    CanRescheduleOrCancelVisitQuery,
    CanRescheduleOrCancelVisitQueryVariables,
} from '@/components/visits/tables/VisitActionsCell.gql.types';
import { WarningsIcon } from '@/components/warnings/WarningsIcon';
import { DialogType, useDialog } from '@/dialogs/DialogManager';
import { useAppConfig } from '@/fetching/api/appConfig';
import {
    VISIT_NO_SERVICES_TOOLTIP,
    useCanCancelVisit,
    useCanReassignVisit,
    useCanRescheduleVisit,
    visitHasServices,
} from '@/hooks/permissions/visit';
import { errorToast, successToast } from '@/toast';
import { VisitOutcome, VisitState, isVisitComplete } from '@/types/visit';
import { useBooleanState } from '@/utils/booleanState';
import { useOpenLinkWithNewTabSupport } from '@/utils/links';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { MoreVert } from '@mui/icons-material';
import { FC, type MouseEvent, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';

export const CanRescheduleOrCancelVisitFragment = gql`
    fragment CanRescheduleOrCancelVisitFragment on Visit {
        id
        state
        outcome
        patient {
            id
        }
        permissions {
            id
            reschedule
            assign
            cancel
            editState
            editOutcome
            editWithCompletedOutcome
        }
        clinicianAppointments {
            id
            services {
                id
            }
        }
        requiresReschedule
        warnings {
            message
        }
    }
`;

const CAN_RESCHEDULE_OR_CANCEL_VISIT_QUERY = gql`
    query CanRescheduleOrCancelVisit($visitId: UUID!) {
        visit(id: $visitId) {
            ...CanRescheduleOrCancelVisitFragment
        }
    }
`;

// Spreads the fragment each of the cell's per-visit queries selects, so a list that spreads
// this one lets them resolve from the cache instead of the network.
export const VisitActionsCellFragment = gql`
    fragment VisitActionsCellFragment on Visit {
        id
        visitState: state
        outcome
        requiresReschedule
        order {
            id
        }
        warnings {
            message
        }
        ...CanRescheduleOrCancelVisitFragment
        ...EditVisitServicesActionFragment
        ...EditFulfillmentWindowActionFragment
    }
`;

const REVIEW_MISSED_VISIT_MUTATION = gql`
    mutation ReviewMissedOrderVisit($visitId: UUID!, $requiresReschedule: Boolean!) {
        markVisitAsMissed(input: { visitId: $visitId, requiresReschedule: $requiresReschedule }) {
            id
            state
            outcome
            requiresReschedule
            order {
                ...OrderStatusFragment
            }
        }
    }
`;

export interface Visit {
    id: string;
    visitState: VisitState;
    outcome: VisitOutcome;
    requiresReschedule: boolean | null;
    order: {
        id: string;
    } | null;
    warnings: { message: string }[];
}

type VisitActionsCellProps = {
    visitId: string;
};

/**
 * Builds the visit "more actions" menu (Book/Reschedule, Update Service, Edit Fulfillment
 * Window, Reassign, Edit, Cancel, View) for a single visit, sharing one source of truth between the
 * table row three-dot menu (VisitActionsCell) and the calendar quick-actions menu
 * (CalendarVisitActionsMenu) so the two can't drift.
 *
 * `onActionSelected` is called when an action is picked — the caller uses it to
 * close its container (the menu), mirroring the row menu's behavior.
 * Cancel opens a local CancelVisitDialog, so the state to render it is returned.
 */
export const useVisitActionsMenu = ({
    visitId,
    onActionSelected,
}: {
    visitId: string;
    onActionSelected: () => void;
}) => {
    const openLinkWithNewTabSupport = useOpenLinkWithNewTabSupport();
    const navigate = useNavigate();
    const appConfig = useAppConfig();
    const [isCancelDialogOpen, onOpenCancelDialog, onCloseCancelDialog] = useBooleanState(false);
    const [reviewMissedVisit] = useMutation(REVIEW_MISSED_VISIT_MUTATION);
    const { data } = useQuery<
        CanRescheduleOrCancelVisitQuery,
        CanRescheduleOrCancelVisitQueryVariables
    >(CAN_RESCHEDULE_OR_CANCEL_VISIT_QUERY, {
        variables: {
            visitId,
        },
    });
    const { openDialog } = useDialog();
    const {
        canEditServices,
        label: editServicesLabel,
        open: openEditServicesDialog,
    } = useEditVisitServicesAction(visitId);
    const { canEditFulfillmentWindow, open: openEditFulfillmentWindowDialog } =
        useEditFulfillmentWindowAction(visitId);
    const canReassignVisit = useCanReassignVisit(data?.visit);
    const hasServices = visitHasServices(data?.visit);
    const canRescheduleVisit = useCanRescheduleVisit(data?.visit);
    const canCancelVisit = useCanCancelVisit(data?.visit ?? null);

    // Edit permission: can edit if the dialog is enabled and the visit is editable
    // (not complete OR has editWithCompletedOutcome permission)
    const canEditVisit =
        appConfig.visit.edit.dialog &&
        data?.visit &&
        (!isVisitComplete(data.visit.state) || data.visit.permissions.editWithCompletedOutcome);

    const onChangeVisit = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            onActionSelected();
            openLinkWithNewTabSupport(`/visits/${visitId}/schedule`, event);
        },
        [onActionSelected, openLinkWithNewTabSupport, visitId],
    );

    const onReassignVisit = useCallback(() => {
        onActionSelected();
        openDialog(DialogType.ReassignVisits, {
            visitsToReassign: [visitId],
            cliniciansToReassign: [],
            entrySource: EntrySource.VisitActionsMenu,
        });
    }, [onActionSelected, openDialog, visitId]);

    const onRescheduleVisit = useCallback(() => {
        reviewMissedVisit({
            variables: {
                visitId: visitId,
                requiresReschedule: data?.visit?.requiresReschedule,
            },
        })
            .then(() => {
                successToast('Unable to reschedule visit.');
            })
            .catch(() => {
                errorToast('Unable to reschedule visit.');
            });
    }, [reviewMissedVisit, visitId, data?.visit?.requiresReschedule]);

    const onCancelVisit = useCallback(() => {
        onActionSelected();
        onOpenCancelDialog();
    }, [onActionSelected, onOpenCancelDialog]);

    const onEditVisit = useCallback(() => {
        onActionSelected();
        openDialog(DialogType.EditVisit, { visitId: visitId });
    }, [onActionSelected, openDialog, visitId]);

    const onEditServices = useCallback(() => {
        onActionSelected();
        openEditServicesDialog();
    }, [onActionSelected, openEditServicesDialog]);

    const onEditFulfillmentWindow = useCallback(() => {
        onActionSelected();
        openEditFulfillmentWindowDialog();
    }, [onActionSelected, openEditFulfillmentWindowDialog]);

    const items = useMemo(() => {
        const menuItems: MenuItemInfo[] = [];
        if (canRescheduleVisit) {
            if (data?.visit?.requiresReschedule) {
                menuItems.push({
                    label: 'Requires Reschedule',
                    onClick: onRescheduleVisit,
                });
            } else {
                switch (data?.visit?.state) {
                    case VisitState.CONFIRMED:
                        menuItems.push({
                            label: 'Reschedule',
                            onClick: onChangeVisit,
                            disabled: !hasServices,
                            tooltip: !hasServices ? VISIT_NO_SERVICES_TOOLTIP : undefined,
                        });
                        break;
                    default:
                        menuItems.push({
                            label: 'Book',
                            onClick: onChangeVisit,
                            disabled: !hasServices,
                            tooltip: !hasServices ? VISIT_NO_SERVICES_TOOLTIP : undefined,
                        });
                        break;
                }
            }
        }
        if (canEditServices) {
            menuItems.push({
                label: editServicesLabel,
                onClick: onEditServices,
            });
        }
        if (canEditFulfillmentWindow) {
            menuItems.push({
                label: 'Edit Fulfillment Window',
                onClick: onEditFulfillmentWindow,
            });
        }
        if (canReassignVisit) {
            // A service is required: the reassign flow's clinician and date
            // recommendations are computed from it.
            const reassignDisabled = !hasServices;
            menuItems.push({
                label: 'Reassign',
                onClick: onReassignVisit,
                disabled: reassignDisabled,
                tooltip: reassignDisabled ? VISIT_NO_SERVICES_TOOLTIP : undefined,
            });
        }
        if (canEditVisit) {
            menuItems.push({
                label: 'Edit',
                onClick: onEditVisit,
            });
        }

        return menuItems.concat([
            {
                condition: canCancelVisit,
                label: 'Cancel',
                onClick: onCancelVisit,
            },
            {
                label: 'View',
                onClick: () => {
                    onActionSelected();
                    void navigate(`/visits/${visitId}`);
                },
            },
        ]);
    }, [
        canCancelVisit,
        canEditFulfillmentWindow,
        canEditServices,
        canEditVisit,
        canReassignVisit,
        canRescheduleVisit,
        onActionSelected,
        editServicesLabel,
        hasServices,
        navigate,
        onCancelVisit,
        onChangeVisit,
        onEditFulfillmentWindow,
        onEditServices,
        onEditVisit,
        onReassignVisit,
        onRescheduleVisit,
        visitId,
        data?.visit?.requiresReschedule,
        data?.visit?.state,
    ]);

    return {
        items,
        isCancelDialogOpen,
        onCloseCancelDialog,
        warnings: data?.visit?.warnings,
    };
};

export const VisitActionsCell: FC<VisitActionsCellProps> = ({ visitId }) => {
    const [isMenuOpen, openMenu, closeMenu] = useBooleanState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const { items, isCancelDialogOpen, onCloseCancelDialog, warnings } = useVisitActionsMenu({
        visitId,
        onActionSelected: closeMenu,
    });

    return (
        <>
            <AxleMenu
                isOpen={isMenuOpen}
                close={closeMenu}
                anchorEl={buttonRef.current}
                onClick={(event) => {
                    // Prevent all clicks from within these or on the backdrop brought up with this menu
                    // from triggering the table row
                    event.stopPropagation();
                }}
                items={items}
            />
            <AxleIconButton
                ref={buttonRef}
                icon={<MoreVert />}
                tooltip={'More actions'}
                iconButtonStyle={{ p: 0.5 }}
                onClick={(event) => {
                    // Prevent from propagating to table row click events.
                    event.stopPropagation();
                    openMenu();
                }}
            />
            <HorizontalStack
                onClick={(event) => {
                    // Prevent clicks, including those inside the portaled dialog, from
                    // propagating to the table row.
                    event.stopPropagation();
                }}
            >
                <WarningsIcon warnings={warnings} />
                <CancelVisitDialog
                    isOpen={isCancelDialogOpen}
                    onClose={onCloseCancelDialog}
                    visitId={visitId}
                />
            </HorizontalStack>
        </>
    );
};
